import { StyleSheet, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { formatTimeFromSeconds } from '../../helpers/durationParser';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { useEpisodeProgress } from '../../hooks/useEpisodeProgress';
import { useIsEpisodeRead } from '../../hooks/useReadEpisodes';
import { formatEpisodeDate } from '../../localization/datetime';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel } from '../../models';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { PlayPauseButton } from '../PlayPauseButton';

export function EpisodeControlsSection(episode: PodcastEpisodeModel) {
  const divRadius = useAppSelector(selectDivRadius);

  // dynamic font sizes
  const textFontSize = useAdjustedFontSize(mvs(14));
  const isRead = useIsEpisodeRead(episode.podcastId, episode.id);

  // when the episode is in progress, show the remaining time (with a minus)
  // instead of the total duration (same behaviour as the play buttons)
  const { isInProgress, remainingSeconds } = useEpisodeProgress(episode);

  return (
    <View style={[styles.episodeControls, { borderRadius: divRadius }]}>
      <View style={styles.controlsContent}>
        {/* date */}
        <View style={styles.metaItem}>
          <CustomIcon name="calendar" size={mvs(20)} style={styles.metaIcon} />
          <CustomText
            style={[styles.metaValue, { fontSize: textFontSize }]}
            numberOfLines={1}
          >
            {formatEpisodeDate(episode.date)}
          </CustomText>
        </View>

        {/* read indicator */}
        {isRead && (
          <View style={styles.readIndicator}>
            <CustomText style={{ fontSize: textFontSize }}>
              {i18n.t('podcast.read')}
            </CustomText>
            <CustomIcon name="check" size={mvs(12)} />
          </View>
        )}

        {/* duration */}
        <View style={styles.metaItem}>
          <CustomIcon name="clock" size={mvs(20)} style={styles.metaIcon} />
          <CustomText
            style={[styles.metaValue, { fontSize: textFontSize }]}
            numberOfLines={2}
          >
            {isInProgress
              ? `-${formatTimeFromSeconds(remainingSeconds)}`
              : episode.duration}
          </CustomText>
        </View>
      </View>
      {/* play button overlay */}
      <View style={styles.playButtonContainer}>
        <PlayPauseButton size={52} iconSize={18} podcastEpisode={episode} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  episodeControls: {
    backgroundColor: COLORS.WHITE,
    marginBottom: mvs(10),
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  controlsContent: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    flex: 1,
    paddingHorizontal: mvs(12),
    paddingVertical: mvs(16),
  },
  metaItem: {
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  metaIcon: {
    marginBottom: mvs(4),
    opacity: 0.4,
  },
  metaValue: {
    color: '#252525',
    textAlign: 'center',
  },
  readIndicator: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    alignItems: 'center',
    gap: mvs(4),
    marginHorizontal: mvs(16),
    opacity: 0.5,
  },
  playButtonContainer: {
    position: 'absolute',
    top: -mvs(29),
  },
});
